/**
 * The fixed-timestep simulation driver and the per-tick step() function.
 *
 * step() is the single authoritative update: it is deterministic, pure with
 * respect to the World it is given, and framerate-independent. The Simulation
 * class accumulates real elapsed time and runs as many fixed steps as needed,
 * exposing an interpolation `alpha` so the renderer can draw smoothly between
 * ticks. The same step() can run on a server at the same fixed rate later.
 */
import type { World } from './world';
import { clone } from './math/vec2';
import { applyMovement } from './systems/movement';
import { updateBots } from './systems/bots';
import { resolveEating, type EatEvent } from './systems/eating';

export interface StepResult {
  eatEvents: EatEvent[];
  /** True if the player entity died during this step. */
  playerDied: boolean;
}

/** Advance the world by exactly one fixed timestep. */
export function step(world: World): StepResult {
  const cfg = world.cfg;
  const dt = cfg.world.fixedTimestep;
  world.time += dt;

  // Snapshot positions for render interpolation, then run systems.
  const alive = world.entities.filter((e) => e.alive);
  for (const e of alive) e.prevPos = clone(e.pos);

  // 1) Bots perceive the (pre-movement) world and emit their intents.
  world.spatial.rebuild(alive);
  updateBots(world.entities, world.spatial, world.byId, cfg, world.time, dt, world.rng);

  // 2) Apply actions (grow/shrink) then movement for every entity.
  for (const e of alive) {
    for (const action of world.actions) {
      action.apply({ entity: e, cfg, dt, now: world.time });
    }
    applyMovement(e, cfg, dt);
    if (e.boostTimer > 0) e.boostTimer = Math.max(0, e.boostTimer - dt);
    if (e.size > e.peakSize) e.peakSize = e.size;
  }

  // 3) Resolve eating against post-movement positions.
  world.spatial.rebuild(world.entities.filter((e) => e.alive));
  const eatEvents = resolveEating(world.entities, world.spatial, cfg);

  const player = world.player;
  const playerDied = player != null && !player.alive;

  // 4) Clean up the dead and keep the arena populated.
  world.reapDead();
  world.maintainBotPopulation();

  return { eatEvents, playerDied };
}

export class Simulation {
  readonly world: World;
  private accumulator = 0;
  private alphaValue = 0;

  /** Called once per fixed step (may fire multiple times per frame). */
  onStep?: (result: StepResult) => void;

  constructor(world: World) {
    this.world = world;
  }

  /** Advance by the real elapsed wall-clock time (in milliseconds). */
  advance(elapsedMs: number): void {
    const dt = this.world.cfg.world.fixedTimestep;
    const maxSteps = this.world.cfg.world.maxStepsPerFrame;
    this.accumulator += elapsedMs / 1000;

    let steps = 0;
    while (this.accumulator >= dt && steps < maxSteps) {
      const result = step(this.world);
      this.onStep?.(result);
      this.accumulator -= dt;
      steps++;
    }
    // If we maxed out the step budget, drop the backlog to avoid a death spiral.
    if (steps >= maxSteps && this.accumulator > dt) this.accumulator = 0;

    this.alphaValue = this.accumulator / dt;
  }

  /** Interpolation factor in [0, 1) for rendering between fixed steps. */
  get alpha(): number {
    return this.alphaValue;
  }
}
